import { useEffect, useState } from 'react';
import { apiFetch } from '../api';

/**
 * Fetch release metadata from GET /system/version.
 *
 * Never throws: an unreachable backend yields status 'error' so callers can
 * fall back to doing nothing. Pass enabled=false to skip the request.
 */
export default function useReleaseNotes({ since, enabled = true } = {}) {
  const [state, setState] = useState({
    status: enabled ? 'loading' : 'idle',
    latestVersion: null,
    minSupported: null,
    releases: [],
  });

  useEffect(() => {
    if (!enabled) {
      setState((prev) => (prev.status === 'idle' ? prev : { ...prev, status: 'idle' }));
      return undefined;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading' }));

    const query = since ? `?since=${encodeURIComponent(since)}` : '';
    (async () => {
      try {
        const response = await apiFetch(`/system/version${query}`, {}, { background: true, skipAuth: true });
        if (!response?.ok) throw new Error(`Release notes request failed (${response?.status})`);
        const body = await response.json();
        if (cancelled) return;
        setState({
          status: 'ready',
          latestVersion: body?.latest_version || null,
          minSupported: body?.min_supported_client_version || null,
          releases: Array.isArray(body?.releases) ? body.releases : [],
        });
      } catch (error) {
        if (!cancelled) {
          setState({ status: 'error', latestVersion: null, minSupported: null, releases: [] });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [since, enabled]);

  return state;
}
